import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { api } from '../../api/client';
export default function BuildStep({ onAdvance, onChanged }) {
    const [candidates, setCandidates] = useState(null);
    const [existing, setExisting] = useState([]);
    const [chosen, setChosen] = useState(null);
    const [instruction, setInstruction] = useState('');
    const [outline, setOutline] = useState(null);
    const [outlineTitle, setOutlineTitle] = useState('');
    const [busy, setBusy] = useState('load');
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    async function refresh() {
        setBusy('load');
        setError(null);
        try {
            const r = await api.wizardBuildState();
            setCandidates(r.candidates);
            setExisting(r.existing);
        }
        catch (e) {
            setError(e?.message ?? 'Failed to load');
        }
        finally {
            setBusy(null);
            onChanged();
        }
    }
    useEffect(() => { refresh(); }, []);
    async function generateOutline() {
        if (!chosen)
            return;
        setBusy('outline');
        setError(null);
        try {
            const r = await api.wizardPageOutline({
                slug: chosen.slug,
                title: chosen.title,
                instruction,
                apply: true,
            });
            setOutline(r.sections);
            setOutlineTitle(r.title || chosen.title);
            setRows(r.sections.map((s) => ({ section: s, status: 'pending' })));
            onChanged();
        }
        catch (e) {
            setError(e?.message ?? 'Outline failed');
        }
        finally {
            setBusy(null);
        }
    }
    async function generateAllSections() {
        if (!chosen || !outline)
            return;
        setBusy('sections');
        setError(null);
        try {
            // Sequential generation — each call may take 10–30s.
            for (let i = 0; i < outline.length; i++) {
                const s = outline[i];
                setRows((rs) => rs.map((r, j) => j === i ? { ...r, status: 'working' } : r));
                try {
                    const res = await api.wizardSection({
                        slug: chosen.slug,
                        block_id: s.id,
                        kind: s.kind,
                        type: s.type,
                        intent: s.intent,
                        instruction,
                        apply: true,
                    });
                    setRows((rs) => rs.map((r, j) => j === i ? { ...r, status: 'done', notes: res.notes } : r));
                }
                catch (err) {
                    setRows((rs) => rs.map((r, j) => j === i ? { ...r, status: 'error', error: err?.message ?? 'failed' } : r));
                }
            }
            onChanged();
        }
        finally {
            setBusy(null);
        }
    }
    async function regenerateOne(idx) {
        if (!chosen || !outline)
            return;
        const s = outline[idx];
        setRows((rs) => rs.map((r, j) => j === idx ? { ...r, status: 'working', error: undefined } : r));
        try {
            const res = await api.wizardSection({
                slug: chosen.slug, block_id: s.id, kind: s.kind, type: s.type,
                intent: s.intent, instruction, apply: true,
            });
            setRows((rs) => rs.map((r, j) => j === idx ? { ...r, status: 'done', notes: res.notes, error: undefined } : r));
        }
        catch (err) {
            setRows((rs) => rs.map((r, j) => j === idx ? { ...r, status: 'error', error: err?.message ?? 'failed' } : r));
        }
    }
    return (_jsxs("div", { className: "step-content", children: [_jsxs("section", { className: "card-section", children: [_jsx("h2", { children: "Pick a page to build" }), !candidates && _jsx("div", { className: "muted", children: "Loading\u2026" }), candidates && candidates.length === 0 && (_jsxs("div", { className: "callout", children: [_jsx("strong", { children: "No candidates from site-plan.md." }), _jsx("span", { className: "muted small", children: "Run the Plan step first, or pick an existing page below." })] })), candidates && candidates.length > 0 && (_jsx("ul", { className: "page-pick-list", children: candidates.map((c) => (_jsx("li", { children: _jsxs("label", { children: [_jsx("input", { type: "radio", name: "build-page", checked: chosen?.slug === c.slug, onChange: () => { setChosen(c); setOutline(null); setRows([]); } }), _jsxs("span", { className: "pick-slug", children: ["/", c.slug === 'home' ? '' : c.slug] }), _jsx("span", { className: "pick-title", children: c.title }), c.has_page && _jsx("span", { className: "tag", children: "exists" })] }) }, c.slug))) })), existing.length > 0 && (_jsxs("p", { className: "muted small", style: { marginTop: '0.6rem' }, children: ["Existing pages on disk: ", existing.map((s) => `/${s === 'home' ? '' : s}`).join(', ')] }))] }), chosen && (_jsxs("section", { className: "card-section", children: [_jsx("h2", { children: "Outline" }), _jsxs("label", { className: "field", children: [_jsx("span", { children: "Optional instruction (steers both outline and section generation)" }), _jsx("textarea", { rows: 2, value: instruction, onChange: (e) => setInstruction(e.target.value), placeholder: "Be punchy and specific to a Bangalore audience\u2026" })] }), _jsx("div", { className: "step-foot", children: _jsx("button", { onClick: generateOutline, disabled: busy !== null, children: busy === 'outline' ? 'Generating…' : `${outline ? 'Re-generate' : 'Generate'} outline for /${chosen.slug === 'home' ? '' : chosen.slug}` }) }), outline && (_jsxs(_Fragment, { children: [_jsx("h3", { style: { marginTop: '1rem' }, children: outlineTitle }), _jsx("ul", { className: "outline-list", children: rows.map((r, i) => (_jsxs("li", { className: `outline-row outline-${r.status}`, children: [_jsx("span", { className: "outline-pos", children: i + 1 }), _jsxs("div", { className: "outline-body", children: [_jsxs("div", { children: [_jsx("code", { children: r.section.id }), _jsx("span", { className: "tag", children: r.section.kind }), _jsx("span", { className: "muted small", children: r.section.type })] }), _jsx("div", { className: "muted small", children: r.section.intent }), r.error && _jsx("div", { className: "err small", children: r.error }), r.notes && _jsx("div", { className: "muted small", children: r.notes })] }), _jsxs("div", { className: "outline-status", children: [r.status === 'pending' && _jsx("span", { className: "muted small", children: "queued" }), r.status === 'working' && _jsx("span", { className: "muted small", children: "working\u2026" }), r.status === 'done' && _jsx("span", { className: "ok small", children: "\u2713 done" }), r.status === 'error' && _jsx("button", { className: "ghost small", onClick: () => regenerateOne(i), children: "Retry" })] })] }, r.section.id))) }), _jsx("div", { className: "step-foot", children: _jsx("button", { onClick: generateAllSections, disabled: busy !== null || rows.every((r) => r.status === 'done'), children: busy === 'sections' ? 'Generating sections (sequential, may take a while)…' : 'Generate all sections' }) })] }))] })), error && _jsx("div", { className: "err small pad-y", children: error }), _jsx("div", { className: "step-foot", children: _jsx("button", { onClick: onAdvance, children: "Continue \u2192" }) })] }));
}
